import React, { useState, useMemo, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { Search, X, ArrowDownUp, Loader2, Upload, Repeat2 } from 'lucide-react';
import { ImportStickersModal } from '../components/ImportStickersModal';
import { ResetDuplicatesModal } from '../components/ResetDuplicatesModal';
import { ALL_TEAMS, getAllStickerCodes, getTeamStickerCodes, type Team } from '../data/stickers';
import { groupTeamsByConfederation } from '../data/confederations';
import { TeamSection, type Filter } from '../components/ui/TeamSection';
import { StatCard } from '../components/ui/StatCard';
import { Toast } from '../components/ui/Toast';
import type { StickerStats } from '../hooks/useStickers';
import { useWishlist } from '../hooks/useWishlist';
import { PriorityLimitModal } from '../components/PriorityLimitModal';
import { track } from '../lib/track';

interface ToastState {
  message: string;
  action?: { label: string; onClick: () => void };
  error?: boolean;
}

interface Props {
  user: User;
  counts: Record<string, number>;
  loading: boolean;
  stats: StickerStats;
  onStickerClick: (code: string) => void;
  onStickerReset: (code: string) => void;
  onBulkImport: (updates: Array<{ code: string; count: number }>) => Promise<{ applied: number }>;
  onResetDuplicates: () => Promise<Map<string, number>>;
  onRestoreFromSnapshot: (snapshot: Map<string, number>) => Promise<void>;
}

type SortBy = 'default' | 'progress';

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'missing', label: 'Faltantes' },
  { value: 'duplicates', label: 'Repetidas' },
];

function getFirstName(user: User): string {
  const fullName = (user.user_metadata?.full_name as string) || '';
  if (fullName) return fullName.split(' ')[0];
  if (user.email) return user.email.split('@')[0];
  return 'colecionador';
}

function teamMatches(team: Team, q: string): boolean {
  if (!q) return true;
  const upper = q.toUpperCase();
  return (
    team.name.toUpperCase().includes(upper) ||
    team.code.includes(upper) ||
    getTeamStickerCodes(team.code).some((c) => c.includes(upper))
  );
}

export function AlbumTab({
  user,
  counts,
  loading,
  stats,
  onStickerClick,
  onStickerReset,
  onBulkImport,
  onResetDuplicates,
  onRestoreFromSnapshot,
}: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sortBy, setSortBy] = useState<SortBy>('default');
  const [importOpen, setImportOpen] = useState(false);
  const [resetDupesOpen, setResetDupesOpen] = useState(false);
  const [resetDupesLoading, setResetDupesLoading] = useState(false);
  const [toastState, setToastState] = useState<ToastState | null>(null);
  const validCodes = useMemo(() => {
    const set = new Set(getAllStickerCodes());
    getTeamStickerCodes('CC').forEach((c) => set.add(c));
    return set;
  }, []);
  const totalExtras = useMemo(
    () =>
      Object.values(counts)
        .filter((c) => c > 1)
        .reduce((sum, c) => sum + (c - 1), 0),
    [counts]
  );

  const affectedTeams = useMemo(() => {
    const prefixes = new Set<string>();
    for (const [code, count] of Object.entries(counts)) {
      if (count > 1) {
        const m = code.match(/^[A-Z]+/);
        if (m) prefixes.add(m[0]);
      }
    }
    return prefixes.size;
  }, [counts]);

  const handleConfirmResetDupes = useCallback(async () => {
    setResetDupesLoading(true);
    const capturedExtras = totalExtras;
    const capturedTeams = affectedTeams;
    try {
      const snapshot = await onResetDuplicates();
      setResetDupesOpen(false);
      setResetDupesLoading(false);
      void track('duplicates_reset', { count: capturedExtras, teams: capturedTeams });
      const msg =
        capturedExtras === 1
          ? '1 repetida marcada como trocada.'
          : `${capturedExtras} repetidas marcadas como trocadas.`;
      setToastState({
        message: msg,
        action: {
          label: 'Desfazer',
          onClick: async () => {
            try {
              await onRestoreFromSnapshot(snapshot);
              setToastState({ message: 'Operação desfeita.' });
            } catch {
              setToastState({ message: 'Não foi possível desfazer. Tente novamente.', error: true });
            }
          },
        },
      });
    } catch {
      setResetDupesLoading(false);
      setToastState({ message: 'Não foi possível realizar a operação. Tente novamente.', error: true });
    }
  }, [totalExtras, affectedTeams, onResetDuplicates, onRestoreFromSnapshot]);

  const { wishlist, byCode: wishlistByCode, add: addPriority, remove: removePriority } = useWishlist(user.id);
  const [pendingPriorityCode, setPendingPriorityCode] = useState<string | null>(null);

  const firstName = getFirstName(user);

  const handleTogglePriority = useCallback(
    async (code: string) => {
      if (wishlistByCode[code]) {
        await removePriority(code);
        return;
      }
      const result = await addPriority(code);
      if (!result.ok && result.reason === 'limit_reached') {
        setPendingPriorityCode(code);
      }
    },
    [wishlistByCode, addPriority, removePriority]
  );

  const handlePickToRemove = useCallback(
    async (codeToRemove: string) => {
      await removePriority(codeToRemove);
      if (pendingPriorityCode) {
        await addPriority(pendingPriorityCode);
        setPendingPriorityCode(null);
      }
    },
    [removePriority, addPriority, pendingPriorityCode]
  );

  const getTeamProgress = (code: string): number => {
    const codes = getTeamStickerCodes(code);
    return codes.filter((c) => (counts[c] || 0) >= 1).length / codes.length;
  };

  const groups = useMemo(() => groupTeamsByConfederation(), []);
  const PRE_PINNED = ['FWC_COPA', 'FWC_BOLAS'];
  const POST_PINNED = ['FWC_HIST', 'EXTRAS', 'CC'];
  const PINNED = [...PRE_PINNED, ...POST_PINNED];
  const prePinnedTeams = PRE_PINNED.map(
    (code) => ALL_TEAMS.find((t) => t.code === code)!
  );
  const postPinnedTeams = POST_PINNED.map(
    (code) => ALL_TEAMS.find((t) => t.code === code)!
  );

  const flatSorted = useMemo(() => {
    const main = ALL_TEAMS.filter((t) => !PINNED.includes(t.code));
    return [...main].sort((a, b) => getTeamProgress(b.code) - getTeamProgress(a.code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts]);

  const sectionVisible = (team: Team): boolean => {
    if (!teamMatches(team, searchQuery)) return false;
    if (filter !== 'all') {
      const codes = getTeamStickerCodes(team.code);
      const visible = codes.filter((c) => {
        if (filter === 'missing') return (counts[c] || 0) === 0;
        if (filter === 'duplicates') return (counts[c] || 0) >= 2;
        return true;
      });
      if (visible.length === 0) return false;
    }
    return true;
  };

  const noResults =
    !!searchQuery &&
    ALL_TEAMS.every((team) => {
      if (!teamMatches(team, searchQuery)) return true;
      const codes = getTeamStickerCodes(team.code).filter((c) => {
        if (filter === 'missing') return (counts[c] || 0) === 0;
        if (filter === 'duplicates') return (counts[c] || 0) >= 2;
        return true;
      });
      const upper = searchQuery.toUpperCase();
      return (
        !team.name.toUpperCase().includes(upper) &&
        !team.code.includes(upper) &&
        !codes.some((c) => c.includes(upper))
      );
    });

  const renderTeam = (team: Team) => (
    <TeamSection
      key={team.code}
      team={team}
      counts={counts}
      onStickerClick={onStickerClick}
      onStickerReset={onStickerReset}
      searchQuery={searchQuery}
      filter={filter}
      wishlistByCode={wishlistByCode}
      onTogglePriority={handleTogglePriority}
    />
  );

  return (
    <div className="pb-4">
      {/* Greeting */}
      <header className="mb-6">
        <p className="bt-eyebrow text-green-700 mb-2">Olá, {firstName}</p>
        <h1 className="font-display font-bold text-h1 md:text-display leading-tight text-ink-900 tracking-[-0.02em]">
          Faltam <span className="text-green-700 tabular-nums">{stats.missing}</span> pra fechar.
        </h1>
        <p className="font-body text-body text-ink-700 mt-2">
          {stats.percentage.toFixed(1)}% concluído · {stats.owned} de {stats.total}
        </p>
      </header>

      {/* Top progress strip */}
      <div className="mb-6">
        <div className="h-2 bg-ink-100 rounded-pill overflow-hidden">
          <div
            className="h-full rounded-pill transition-[width] duration-progress ease-out"
            style={{
              width: `${stats.percentage}%`,
              background: 'linear-gradient(90deg, var(--green-700), var(--gold-500))',
            }}
          />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard accent="green" label="Tenho" value={stats.owned} sublabel={`de ${stats.total}`} />
        <StatCard accent="ruby" label="Faltam" value={stats.missing} sublabel="para completar" />
        <StatCard accent="gold" label="Repetidas" value={stats.duplicates} sublabel="pra trocar" />
        <StatCard accent="ruby" label="Coca-Cola" value={`${stats.ccOwned}/14`} sublabel="para completar" />
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search
          size={18}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"
        />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar seleção, código ou número (ex: BRA, BRA10, França)"
          className="w-full pl-10 pr-10 h-12 rounded-lg border border-ink-300 bg-white text-body text-ink-900 placeholder:text-ink-500 transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            aria-label="Limpar busca"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-900 btn-focus rounded-md p-1"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filter + Sort */}
      <div className="flex items-center justify-between mb-6 gap-2 flex-wrap">

        <div className="inline-flex p-0.5 bg-ink-100 rounded-lg">
          {FILTER_OPTIONS.map((opt) => {
            const active = filter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={[
                  'px-3.5 py-1.5 rounded-md text-caption font-semibold font-body transition-colors btn-focus',
                  active
                    ? 'bg-white text-green-900 shadow-xs'
                    : 'text-ink-500 hover:text-ink-900',
                ].join(' ')}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {totalExtras > 0 && (
            <button
              type="button"
              onClick={() => setResetDupesOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-caption font-semibold font-body transition-colors btn-focus bg-gold-500 text-ink-900 border-gold-500 hover:bg-gold-600 hover:border-gold-600"
            >
              <Repeat2 size={14} strokeWidth={2.25} />
              Já troquei minhas repetidas
            </button>
          )}
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-caption font-semibold font-body transition-colors btn-focus bg-green-700 text-white border-green-700 hover:bg-green-800"
          >
            <Upload size={14} strokeWidth={2.25} />
            Importar de outro app
          </button>
          <button
            type="button"
            onClick={() => setSortBy((s) => (s === 'default' ? 'progress' : 'default'))}
            className={[
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-caption font-semibold font-body transition-colors btn-focus',
              sortBy === 'progress'
                ? 'bg-green-700 text-white border-green-700'
                : 'bg-white text-ink-700 border-ink-300 hover:border-green-700',
            ].join(' ')}
          >
            <ArrowDownUp size={14} strokeWidth={2.25} />
            {sortBy === 'progress' ? 'Por progresso' : 'Padrão'}
          </button>
        </div>
      </div>

      <ImportStickersModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        currentCounts={counts}
        validCodes={validCodes}
        onApply={onBulkImport}
      />

      {resetDupesOpen && (
        <ResetDuplicatesModal
          totalExtras={totalExtras}
          affectedTeams={affectedTeams}
          loading={resetDupesLoading}
          onConfirm={handleConfirmResetDupes}
          onClose={() => {
            if (!resetDupesLoading) setResetDupesOpen(false);
          }}
        />
      )}

      {toastState && (
        <Toast
          key={toastState.message}
          message={toastState.message}
          onClose={() => setToastState(null)}
          durationMs={toastState.action ? 20000 : 3000}
          action={toastState.action}
          error={toastState.error}
        />
      )}

      {/* Sections */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 text-ink-500">
          <Loader2 size={32} className="animate-spin mb-3 text-green-700" />
          <p className="font-body text-small">Carregando figurinhas...</p>
        </div>
      ) : sortBy === 'progress' ? (
        <div>
          {prePinnedTeams.filter(sectionVisible).map(renderTeam)}
          {flatSorted.filter(sectionVisible).map(renderTeam)}
          {postPinnedTeams.filter(sectionVisible).map(renderTeam)}
          {noResults && (
            <p className="text-center text-ink-500 py-8 text-small">
              Nenhuma figurinha encontrada para "{searchQuery}"
            </p>
          )}
        </div>
      ) : (
        <div>
          {prePinnedTeams.some(sectionVisible) && (
            <p className="bt-eyebrow text-green-700 mb-3 px-1">
              Abertura · Copa 2026
            </p>
          )}
          {prePinnedTeams.filter(sectionVisible).map(renderTeam)}
          {groups.map((g) => {
            const visible = g.teams.filter(sectionVisible);
            if (visible.length === 0) return null;
            return (
              <div key={g.code} className="mb-6">
                <div className="flex items-baseline justify-between mb-3 px-1">
                  <p className="bt-eyebrow text-green-700">
                    {g.label}
                  </p>
                  <span className="font-body text-caption text-ink-500">
                    {g.teams.length} {g.teams.length === 1 ? 'seleção' : 'seleções'}
                  </span>
                </div>
                {visible.map(renderTeam)}
              </div>
            );
          })}
          {postPinnedTeams.some(sectionVisible) && (
            <p className="bt-eyebrow text-green-700 mb-3 px-1">
              Edição especial · Copa 2026
            </p>
          )}
          {postPinnedTeams.filter(sectionVisible).map(renderTeam)}
          {noResults && (
            <p className="text-center text-ink-500 py-8 text-small">
              Nenhuma figurinha encontrada para "{searchQuery}"
            </p>
          )}
        </div>
      )}

      <PriorityLimitModal
        open={pendingPriorityCode !== null}
        current={wishlist}
        pendingCode={pendingPriorityCode}
        onPick={handlePickToRemove}
        onClose={() => setPendingPriorityCode(null)}
      />
    </div>
  );
}
